/**
 * Copyright Highway9 Networks Inc.
 */
import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  type EntityState,
  type PayloadAction,
} from "@reduxjs/toolkit";
import type { RootState } from "..";
import { CarrierInfo, EdgePayload, operatorService } from "../../services/operator-service";
import { OperatorQuery, OperatorRadioQuery } from "../../types/OperatorQuery";
import { CarrierGatewayCore } from "~/types/CarrierGatewayCore";
import { EdgeConnectedDevice } from "~/types/EdgeConnectedDevice";
import { EdgeConnectedRadio } from "~/types/EdgeConnectedRadio";
import { OBJ_STATUS } from "~/constants";
import type { EdgeWithPool } from "~/types/edge";
import { isPartnerKpiOnlyUser } from "~/helpers/partnerKpiOnly";
import { createSocketPatchReducer } from "../events/socketPatch";
import { edgeState, isStandaloneEdgePoolId } from "./edgeSlice";

export const EDGE_ROLE_SECONDARY = "SECONDARY";
export const SERVICE_CARRIER_GATEWAY = "CARRIER_GATEWAY";

export function isSecondaryEdge(edge?: Pick<EdgeWithPool, "runtimeInfo"> | null) {
  return edge?.runtimeInfo?.controlPlaneInfo?.role === EDGE_ROLE_SECONDARY;
}

/** Matches EdgeTable / buildEdgesWithPool: standalone edges use STANDALONE_EDGES_POOL_ID. */
export function isStandaloneEdge(edge?: { edgePoolId?: string; isEmptyPool?: boolean } | null) {
  return isStandaloneEdgePoolId(edge?.edgePoolId) && edge?.isEmptyPool !== true;
}

export function hasCarrierGatewayService(edge?: Pick<EdgeWithPool, "services"> | null) {
  return edge?.services?.some((s) => s?.name === SERVICE_CARRIER_GATEWAY) ?? false;
}

export type MacroIdCountMap = {
  [plmn: string]: number[];
};

export const operatorsAdapter = createEntityAdapter<OperatorQuery>();
export const edgeOperatorsAdapter = createEntityAdapter<OperatorQuery>();
export const operatorRadiosAdapter = createEntityAdapter<OperatorRadioQuery>({
  selectId: (radio) => radio.id!,
});
export const connectedCoresAdapter = createEntityAdapter<CarrierGatewayCore>();
export const edgeConnectedDevicesAdapter = createEntityAdapter<EdgeConnectedDevice>();
export const edgeConnectedRadiosAdapter = createEntityAdapter<EdgeConnectedRadio>();
export const carrierInfoAdapter = createEntityAdapter<CarrierInfo>();

interface OperatorState {
  open: boolean;
  selectedId: string | null;
  operatorsData: EntityState<OperatorQuery>;
  error?: string | null;
  timers: { [key: string]: number };
  action: { [key: string]: string };
  export: {
    open: boolean;
    id: string | null;
    isRunning: boolean;
  };
  // per Edge data
  edgeOperatorsData: EntityState<OperatorQuery>;
  connectedCoresData: EntityState<CarrierGatewayCore>;

  // Imported operator data for import flow (draft — not selection from collection)
  importedOperator: OperatorQuery | null;

  // Operator Radio state
  radio: {
    open: boolean;
    selectedId: string | null;
    radiosData: EntityState<OperatorRadioQuery>;
    error?: string | null;
  };

  // Carrier Gateway Edge Connected
  edgeConnectedDevicesData: EntityState<EdgeConnectedDevice>;
  edgeConnectedRadiosData: EntityState<EdgeConnectedRadio>;

  carrierInfoData: EntityState<CarrierInfo>;
}

export const initialState: OperatorState = {
  open: false,
  selectedId: null,
  edgeOperatorsData: edgeOperatorsAdapter.getInitialState(),
  operatorsData: operatorsAdapter.getInitialState(),
  timers: {},
  action: {},
  export: {
    open: false,
    id: null,
    isRunning: false,
  },
  connectedCoresData: connectedCoresAdapter.getInitialState(),

  importedOperator: null,

  radio: {
    open: false,
    selectedId: null,
    radiosData: operatorRadiosAdapter.getInitialState(),
  },

  edgeConnectedDevicesData: edgeConnectedDevicesAdapter.getInitialState(),
  edgeConnectedRadiosData: edgeConnectedRadiosAdapter.getInitialState(),

  carrierInfoData: carrierInfoAdapter.getInitialState(),
};

export const fetchAllOperators = createAsyncThunk("operator/fetchAllOperators", async () => {
  try {
    const response = await operatorService.getAllOperators();
    return response;
  } catch (error) {
    console.log(error);
    throw error;
  }
});

export const fetchOperatorsByEdge = createAsyncThunk("operator/fetchOperatorsByEdge", async (edgeId: string) => {
  const response = await operatorService.getOperatorsByEdge(edgeId);
  return response;
});

export const fetchConnectedCoresByOperator = createAsyncThunk("operator/fetchConnectedCoresByOperator", async (operatorId: string) => {
  const response = await operatorService.getCarrierCore({
    operatorId,
  });
  return response;
});

export const fetchConnectedEdgeDevices = createAsyncThunk("operator/fetchConnectedEdgeDevices", async (payload: EdgePayload) => {
  const response = await operatorService.getConnectedEdgeDevices(payload);
  return response;
});

export const fetchOperatorRadios = createAsyncThunk(
  "operator/fetchOperatorRadios",
  async (operatorIds: string[]) => {
    const requestPayload = {
      operatorIds: operatorIds,
    };
    const response = await operatorService.getOperatorRadios(requestPayload);
    return response;
  },
);

export const fetchCGWEdgeConnectedRadios = createAsyncThunk(
  "operator/fetchCGWEdgeConnectedRadios",
  async (payload: EdgePayload) => {
    try {
      const data = await operatorService.getEdgeConnectedRadios(payload);
      return data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
);

export type FetchCarrierInfoArg = {
  /** When true, run even for RAN_KPI_ONLY / AI_RAN_PARTNER users (e.g. dashboard carrier widgets). */
  skipPartnerKpiOnlyGuard?: boolean;
};

export const fetchCarrierInfo = createAsyncThunk<CarrierInfo[], FetchCarrierInfoArg | void>(
  "operator/fetchCarrierInfo",
  async () => {
    try {
      const response = await operatorService.getCarrierInfo();
      return response;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  // MCS-3777: backend returns 403 for RAN_KPI_ONLY / AI_RAN_PARTNER; carrier info is not surfaced in KPI UI
  // except where explicitly requested (e.g. dashboard).
  {
    condition: (arg) =>
      Boolean((arg as FetchCarrierInfoArg | undefined)?.skipPartnerKpiOnlyGuard) || !isPartnerKpiOnlyUser(),
  },
);

const operatorSlice = createSlice({
  name: "operator",
  initialState,
  reducers: {
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    /** Store selection identity only; resolve the entity via selectors. */
    setSelected: (state, action: PayloadAction<string | null>) => {
      state.selectedId = action.payload;
    },
    setTimer: (state, action: PayloadAction<{ operatorId: string; time: number }>) => {
      state.timers[action.payload.operatorId] = action.payload.time;
    },
    decrementTimer: (state, action: PayloadAction<string>) => {
      if (state.timers[action.payload] > 0) {
        state.timers[action.payload] -= 1;
      }
    },
    setAction: (state, action: PayloadAction<{ operatorId: string; actionType: string }>) => {
      state.action[action.payload.operatorId] = action.payload.actionType;
    },
    setExportOpen: (state, action: PayloadAction<boolean>) => {
      state.export.open = action.payload;
    },
    setExportId: (state, action: PayloadAction<string | null>) => {
      state.export.id = action.payload;
    },
    setExportIsRunning: (state, action: PayloadAction<boolean>) => {
      state.export.isRunning = action.payload;
    },
    setImportedOperator: (state, action: PayloadAction<OperatorQuery | null>) => {
      state.importedOperator = action.payload;
    },

    // Operator Radio reducers
    setRadioOpen: (state, action: PayloadAction<boolean>) => {
      state.radio.open = action.payload;
    },
    /** Store radio selection identity only; resolve via selectors. */
    setRadioSelected: (state, action: PayloadAction<string | null>) => {
      state.radio.selectedId = action.payload;
    },
    applySocketPatchesOperators: createSocketPatchReducer(operatorsAdapter, (s) => s.operatorsData),
    applySocketPatchesEdgeOperators: createSocketPatchReducer(
      edgeOperatorsAdapter,
      (s) => s.edgeOperatorsData,
    ),
    applySocketPatchesOperatorRadios: createSocketPatchReducer(
      operatorRadiosAdapter,
      (s) => s.radio.radiosData,
    ),
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchAllOperators.fulfilled, (state, action) => {
        operatorsAdapter.setAll(state.operatorsData, action.payload);
      })
      .addCase(fetchAllOperators.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(fetchOperatorsByEdge.fulfilled, (state, action) => {
        edgeOperatorsAdapter.setAll(state.edgeOperatorsData, action.payload);
      })
      .addCase(fetchOperatorsByEdge.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(fetchConnectedCoresByOperator.fulfilled, (state, action) => {
        connectedCoresAdapter.setAll(state.connectedCoresData, action.payload.data);
      })
      .addCase(fetchConnectedCoresByOperator.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(fetchOperatorRadios.fulfilled, (state, action) => {
        operatorRadiosAdapter.setAll(state.radio.radiosData, action.payload);
      })
      .addCase(fetchOperatorRadios.rejected, (state, action) => {
        state.radio.error = action.error.message;
      })
      .addCase(fetchConnectedEdgeDevices.fulfilled, (state, action) => {
        edgeConnectedDevicesAdapter.setAll(state.edgeConnectedDevicesData, action.payload.data);
      })
      .addCase(fetchConnectedEdgeDevices.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(fetchCGWEdgeConnectedRadios.fulfilled, (state, action) => {
        edgeConnectedRadiosAdapter.setAll(state.edgeConnectedRadiosData, action.payload.data);
      })
      .addCase(fetchCGWEdgeConnectedRadios.rejected, (state, action) => {
        console.error(action.error);
        edgeConnectedRadiosAdapter.setAll(state.edgeConnectedRadiosData, []);
      })
      .addCase(fetchCarrierInfo.fulfilled, (state, action) => {
        carrierInfoAdapter.setAll(state.carrierInfoData, action.payload);
      })
      .addCase(fetchCarrierInfo.rejected, (state, action) => {
        state.error = action.error.message;
      });
  },
});

export const operatorActions = operatorSlice.actions;
export default operatorSlice.reducer;

const operatorEntitySelectors = operatorsAdapter.getSelectors(
  (state: RootState) => state.operator.operatorsData,
);
const edgeOperatorEntitySelectors = edgeOperatorsAdapter.getSelectors(
  (state: RootState) => state.operator.edgeOperatorsData,
);
const operatorRadioEntitySelectors = operatorRadiosAdapter.getSelectors(
  (state: RootState) => state.operator.radio.radiosData,
);
const connectedCoreEntitySelectors = connectedCoresAdapter.getSelectors(
  (state: RootState) => state.operator.connectedCoresData,
);
const edgeConnectedDeviceEntitySelectors = edgeConnectedDevicesAdapter.getSelectors(
  (state: RootState) => state.operator.edgeConnectedDevicesData,
);
const edgeConnectedRadioEntitySelectors = edgeConnectedRadiosAdapter.getSelectors(
  (state: RootState) => state.operator.edgeConnectedRadiosData,
);
const carrierInfoEntitySelectors = carrierInfoAdapter.getSelectors(
  (state: RootState) => state.operator.carrierInfoData,
);

export const operatorData = operatorEntitySelectors.selectAll;
export const operatorEntities = operatorEntitySelectors.selectEntities;

export const operatorEdgeOperators = edgeOperatorEntitySelectors.selectAll;
export const operatorEdgeEntities = edgeOperatorEntitySelectors.selectEntities;

export const operatorRadiosData = operatorRadioEntitySelectors.selectAll;
export const operatorRadioEntities = operatorRadioEntitySelectors.selectEntities;

export const operatorConnectedCores = connectedCoreEntitySelectors.selectAll;
export const operatorConnectedCoreEntities = connectedCoreEntitySelectors.selectEntities;

export const operatorEdgeConnectedDevices = edgeConnectedDeviceEntitySelectors.selectAll;
export const operatorEdgeConnectedDeviceEntities = edgeConnectedDeviceEntitySelectors.selectEntities;

export const operatorEdgeConnectedRadios = edgeConnectedRadioEntitySelectors.selectAll;
export const operatorEdgeConnectedRadioEntities = edgeConnectedRadioEntitySelectors.selectEntities;

export const operatorCarrierInfo = carrierInfoEntitySelectors.selectAll;
export const operatorCarrierInfoEntities = carrierInfoEntitySelectors.selectEntities;

export const operatorOpen = (state: RootState) => state.operator.open;

/** Resolve selected operator from edge list first, then global operators. */
export const operatorState = createSelector(
  [
    (state: RootState) => state.operator.selectedId,
    operatorEdgeEntities,
    operatorEntities,
  ],
  (selectedId, edgeEntities, entities): OperatorQuery | null => {
    if (!selectedId) return null;
    return edgeEntities[selectedId] ?? entities[selectedId] ?? null;
  },
);

export const operatorRadioState = createSelector(
  [(state: RootState) => state.operator.radio.selectedId, operatorRadioEntities],
  (selectedId, entities): OperatorRadioQuery | null =>
    selectedId ? entities[selectedId] ?? null : null,
);

export const operatorTimer = (state: RootState) => state.operator.timers;
export const operatorAction = (state: RootState) => state.operator.action;
export const operatorExportOpen = (state: RootState) => state.operator.export.open;
export const operatorExportId = (state: RootState) => state.operator.export.id;
export const operatorExportIsRunning = (state: RootState) => state.operator.export.isRunning;
export const operatorImportedData = (state: RootState) => state.operator.importedOperator;

export const operatorRadioOpen = (state: RootState) => state.operator.radio.open;

/**
 * Builds a map of macroIds organized by PLMN, tracking which macroIds are in use.
 * Used to disable macroIds that are already assigned to other operators within the same PLMN.
 */
function buildMacroIdCountMap(operators: OperatorQuery[]): MacroIdCountMap {
  const plmnMacroIdSets: Record<string, Set<number>> = {};

  for (const operator of operators) {
    // If the operator is disabled, its macroIds are considered available for others to use
    if (operator.status === OBJ_STATUS.DISABLED) {
      continue;
    }

    const plmnList = operator.coreConfig.plmn ?? [];
    const macroIdList = operator.coreConfig.macroRadioConfig?.macroDetails?.macroIdList ?? [];

    for (const plmn of plmnList) {
      const set = plmnMacroIdSets[plmn] ??= new Set();
      macroIdList.forEach((id) => set.add(id));
    }
  }
  // Convert Sets to arrays
  return Object.fromEntries(
    Object.entries(plmnMacroIdSets).map(([plmn, ids]) => [plmn, [...ids]]),
  );
}

/** Derived from global operators — which macroIds are already in use per PLMN. */
export const operatorMacroIdCountMap = createSelector(
  [operatorData],
  (operators): MacroIdCountMap => buildMacroIdCountMap(operators),
);

/** Derived from the currently selected edge in edgeSlice. */
export const operatorEdgeContext = createSelector([edgeState], (edge) => ({
  isSecondaryEdge: isSecondaryEdge(edge),
  hasCarrierGatewayService: hasCarrierGatewayService(edge),
  isStandaloneEdge: isStandaloneEdge(edge),
}));

export const operatorEdgeEditLocked = createSelector([operatorEdgeContext],
  ({ isSecondaryEdge, hasCarrierGatewayService, isStandaloneEdge }) => isSecondaryEdge && hasCarrierGatewayService && !isStandaloneEdge,
);
